import { useEffect, useMemo, useRef, useState } from "react";
import {
  MdAssignment,
  MdBusiness,
  MdDescription,
  MdDownload,
  MdPerson,
  MdThumbUp,
  MdWarningAmber,
} from "react-icons/md";
import AppNav from "./components/layout/AppNav";
import { PageContent, PageHeader, PageShell } from "./components/layout/PageShell";
import LoginPage from "./components/auth/LoginPage";
import ForgotPasswordPage from "./components/auth/ForgotPasswordPage";
import ResetPasswordPage from "./components/auth/ResetPasswordPage";
import ConfirmOrganizationDeletionPage from "./components/auth/ConfirmOrganizationDeletionPage";
import PagePSSI from "./features/security/PagePSSI";
import PageConfigDroits from "./features/rights/PageConfigDroits";
import PageROPA from "./features/ropa/PageROPA";
import PageCarteFluxTraitements from "./features/ropa/PageCarteFluxTraitements";
import PageConsentement from "./features/consentement/PageConsentement";
import PageIncidents from "./features/incidents/PageIncidents";
import PageDroits from "./features/droits/PageDroits";
import PageInventaireSI from "./features/inventaire-si/PageInventaireSI";
import PagePRA from "./features/pra/PagePRA";
import PageReportsList from "./features/reports/PageReportsList";
import PageReportsConfig from "./features/reports/PageReportsConfig";
import { reportGenerateSuccessToastMessage } from "./features/reports/reportShared";
import PageTeamConfig from "./features/team/PageTeamConfig";
import {
  initialCategories,
  initialActivites,
  initialSousTraitants,
  initialProofs,
  initialTraitements,
  initialPointsCollecte,
  initialRetraits,
  initialPSSI,
  initialElementsSI,
  initialPRA,
  initialIncidents,
  initialDroits,
  initialProcessusDroits,
} from "./data/seeds";
import PageReferentiels, { PageMatriceRaci, PageRegistreSousTraitants } from "./features/referentiels/PageReferentiels";
import PageDashboard from "./features/dashboard/PageDashboard";
import PageDashboardConfig, { loadDeclarations } from "./features/dashboard/PageDashboardConfig";
import PageSocieteConfig, { loadCompanyProfile, COMPANY_PROFILE_STORAGE_KEY, emptyCompanyProfile } from "./features/societe/PageSocieteConfig";
import HapivotOnboardingWizard from "./features/onboarding/HapivotOnboardingWizard";
import PageAccount from "./features/account/PageAccount";
import { ProofForm, ProofCard, ExportPreview } from "./features/preuves";
import { DocTypesEnabledProvider } from "./shared/DocTypesEnabledContext";
import { DOC_TYPES, DocTypeBadge } from "./shared/docTypes";
import { habColor } from "./shared/habilitations";
import { APP_FONT_FAMILY, COLORS } from "./shared/theme";
import { buildCriticalActions } from "./shared/priorityActions";
import { DEFAULT_SOLUTION_PROCESSING_ACTIVITY } from "./config/defaultProcessingActivity";
import {
  activitiesApi,
  authApi,
  consentApi,
  documentCategoriesApi,
  evidencesApi,
  incidentsApi,
  itAssetsApi,
  personsApi,
  praApi,
  processingActivitiesApi,
  processorsApi,
  pssiApi,
  raciApi,
  reportsApi,
  rightsWorkflowApi,
  requestsApi,
  tokenStorage,
  userStorage,
  tenantStorage,
  organizationsStorage,
  tenantRolesStorage,
  trustedDeviceStorage,
  bootstrapTokenFromUrl,
} from "./services/apiClient";

// ─── DASHBOARD AUDIT EXPRESS ──────────────────────────────────────────────────

function DashboardExpress({
  traitements,
  sousTraitants,
  pointsCollecte,
  droits,
  processusDroits,
  onNavigate,
  onDownloadAuditExpress,
  downloadingAuditReport = false,
}) {
  // Calcul du score sur 4 documents critiques
  const checks = {
    ropa: traitements.length >= 1,
    dpa: sousTraitants.filter(s => s.dpaStatut === "Signé").length >= 1,
    consentements: pointsCollecte.length >= 1,
    droits: processusDroits.conforme, // Le processus est configuré
  };
  const score = Object.values(checks).filter(Boolean).length;

  const docs = [
    { key: "ropa", label: "ROPA — Registre des traitements", done: checks.ropa, desc: traitements.length >= 1 ? `${traitements.length} traitement(s) documenté(s)` : "Aucun traitement documenté", action: "Documenter un traitement", icon: <MdAssignment size={28} />, page: "ropa" },
    { key: "dpa", label: "DPA — Accord sous-traitant principal", done: checks.dpa, desc: checks.dpa ? `${sousTraitants.filter(s => s.dpaStatut === "Signé").length} DPA signé(s)` : "Aucun DPA signé", action: "Ajouter un sous-traitant", icon: <MdBusiness size={28} />, page: "registre-sous-traitants" },
    { key: "consentements", label: "Consentements — Preuve de recueil", done: checks.consentements, desc: pointsCollecte.length >= 1 ? `${pointsCollecte.length} point(s) de collecte documenté(s)` : "Aucun point documenté", action: "Documenter un point de collecte", icon: <MdThumbUp size={28} />, page: "consentement" },
    { key: "droits", label: "Droits — Processus d'exercice", done: checks.droits, desc: checks.droits ? `Processus configuré (${droits.length} demande(s) traitée(s))` : "Processus non configuré", action: "Configurer le processus", icon: <MdPerson size={28} />, page: "droits-config" },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Tableau de bord — Documents essentiels"
        subtitle="Mode Audit Express — complétez rapidement les documents essentiels"
      />

      <PageContent>
        {/* Bandeau info Express */}
        <div style={{ background: "#fef3c7", border: "1.5px solid #fcd34d", borderRadius: 10, padding: "12px 18px", marginBottom: 22, display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ fontSize: 18, display: "inline-flex", alignItems: "center" }}><MdWarningAmber size={18} /></span>
          <div style={{ fontSize: 13, color: "#92400e", lineHeight: 1.6, flex: 1 }}>
            <strong>Mode Audit Express actif</strong> — Cliquez sur les boutons d'action ci-dessous pour compléter rapidement les documents essentiels. Seuls les champs obligatoires vous seront demandés.
          </div>
        </div>

        {/* Score */}
        <div style={{ background: "#fff", borderRadius: 14, border: "1.5px solid #e5e7eb", padding: "24px 32px", marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 90, height: 90, borderRadius: "50%", background: score >= 4 ? "#d1fae5" : score >= 3 ? "#fef3c7" : "#fee2e2", border: `4px solid ${score >= 4 ? "#10b981" : score >= 3 ? "#f59e0b" : "#ef4444"}`, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: score >= 4 ? "#065f46" : score >= 3 ? "#92400e" : "#991b1b", lineHeight: 1 }}>{score}</div>
                <div style={{ fontSize: 11, color: score >= 4 ? "#065f46" : score >= 3 ? "#92400e" : "#991b1b", fontWeight: 700 }}>/ 4</div>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#111827", marginBottom: 4 }}>Score Audit Express</div>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
                {score >= 4 ? "Vous êtes prêt pour un audit CNIL. Maintenez cette documentation à jour." : score >= 3 ? "Presque prêt. Complétez les documents manquants pour sécuriser votre conformité." : "Documents critiques manquants. Complétez-les rapidement pour réduire les risques."}
              </div>
              <div style={{ height: 8, background: "#f1f5f9", borderRadius: 99, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${(score / 4) * 100}%`, background: score >= 4 ? "#10b981" : score >= 3 ? "#f59e0b" : "#ef4444", borderRadius: 99, transition: "width 0.6s" }} />
              </div>
            </div>
          </div>
        </div>

        {/* Checklist */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {docs.map(doc => (
            <div key={doc.key} style={{ background: "#fff", borderRadius: 10, border: `1.5px solid ${doc.done ? "#6ee7b7" : "#fca5a5"}`, padding: 18, display: "flex", alignItems: "center", gap: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
              <div style={{ fontSize: 32 }}>{doc.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 4 }}>{doc.label}</div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>{doc.desc}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {doc.done ? (
                  <div style={{ padding: "8px 16px", borderRadius: 8, background: "#d1fae5", border: "1.5px solid #6ee7b7", fontSize: 13, fontWeight: 700, color: "#065f46" }}>✓ Conforme</div>
                ) : (
                  <>
                    {doc.action && doc.page && <button onClick={() => onNavigate(doc.page, doc.key)} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#dc2626", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>{doc.action}</button>}
                    <div style={{ padding: "8px 16px", borderRadius: 8, background: "#fee2e2", border: "1.5px solid #fca5a5", fontSize: 13, fontWeight: 700, color: "#991b1b", display: "inline-flex", alignItems: "center", gap: 6 }}><MdWarningAmber size={14} /> Manquant</div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Export PDF */}
        <div style={{ marginTop: 24, textAlign: "center" }}>
          <button
            type="button"
            onClick={onDownloadAuditExpress}
            disabled={downloadingAuditReport}
            style={{
              padding: "12px 24px",
              borderRadius: 8,
              border: "none",
              background: "#1e3a5f",
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              cursor: downloadingAuditReport ? "wait" : "pointer",
              opacity: downloadingAuditReport ? 0.8 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <MdDownload size={18} /> {downloadingAuditReport ? "Téléchargement…" : "Télécharger le dossier Audit Express (PDF)"}
          </button>
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 8 }}>Génère puis télécharge un PDF de 5-10 pages présentant vos documents essentiels</div>
        </div>
      </PageContent>
    </PageShell>
  );
}

function buildRaciMatrixPayload(activites, matrix) {
  const fallback = { ...(matrix || {}) };

  (activites || []).forEach((activite) => {
    const actKey = String(activite.id);
    const row = { ...(fallback[actKey] || {}) };
    const hasAnyRole = Object.values(row).some((value) => ["R", "A", "C", "I"].includes(value));

    if (!hasAnyRole && activite.responsableId !== "" && activite.responsableId !== null && activite.responsableId !== undefined) {
      row[String(activite.responsableId)] = "A";
    }

    fallback[actKey] = row;
  });

  return fallback;
}

/** Réponse GET /processing-activities : tableau JSON ou enveloppe { items | data }. */
function normalizeProcessingActivitiesList(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    if (Array.isArray(raw.items)) return raw.items;
    if (Array.isArray(raw.data)) return raw.data;
  }
  return [];
}

function buildDefaultSolutionProcessingActivity() {
  const today = new Date().toISOString().slice(0, 10);
  return {
    id: -1,
    ...DEFAULT_SOLUTION_PROCESSING_ACTIVITY,
    dateCreation: today,
    derniereMaj: today,
  };
}

export default function App() {
  const [authToken, setAuthToken] = useState(() => bootstrapTokenFromUrl() ?? tokenStorage.get());
  const [currentUser, setCurrentUser] = useState(userStorage.get());
  const [sessionReadOnly, setSessionReadOnly] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(true);
  const [page, setPage] = useState(() => {
    const raw = new URLSearchParams(window.location.search).get("page") || "dashboard";
    if (raw === "reports") return "reports-list";
    return raw;
  });
  const [searchHighlight, setSearchHighlight] = useState(null);
  const [mode, setMode] = useState(() => {
    const t = tenantStorage.get();
    return t?.app_mode === "light" ? "express" : t?.app_mode === "complet" ? "complet" : null;
  });
  const [sessionTenant, setSessionTenant] = useState(() => tenantStorage.get());
  const [organizations, setOrganizations] = useState(() => organizationsStorage.get());
  const [tenantRoles, setTenantRoles] = useState(() => tenantRolesStorage.get());
  const [orgMenuBusy, setOrgMenuBusy] = useState(false);
  const [loginSuccessMessage, setLoginSuccessMessage] = useState("");
  const [expressAction, setExpressAction] = useState(null); // {page, action} pour auto-trigger depuis Dashboard Express
  const [proofs, setProofs] = useState(initialProofs);
  const [personnes, setPersonnes] = useState([]);
  const [categories, setCategories] = useState(initialCategories);
  const [activites, setActivites] = useState(initialActivites);
  const [sousTraitants, setSousTraitants] = useState(initialSousTraitants);
  const [incidents, setIncidents] = useState(initialIncidents);
  const [droits, setDroits] = useState(initialDroits);
  const [traitements, setTraitements] = useState(initialTraitements);
  const traitementsRef = useRef(initialTraitements);
  /** Ignore les réponses GET /processing-activities obsolètes (chevauchement bootstrap Strict Mode ou requêtes parallèles). */
  const processingActivitiesFetchGenRef = useRef(0);
  const [pointsCollecte, setPointsCollecte] = useState(initialPointsCollecte);
  const [retraits, setRetraits] = useState(initialRetraits);
  const [processusDroits, setProcessusDroits] = useState(initialProcessusDroits);
  const [raciMatrix, setRaciMatrix] = useState({});
  const [pssi, setPssi] = useState(initialPSSI);
  const [elementsSI, setElementsSI] = useState(initialElementsSI);
  const [pra, setPra] = useState(initialPRA);
  const [showForm, setShowForm] = useState(false);
  const [editingProof, setEditingProof] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [downloadingExpressReport, setDownloadingExpressReport] = useState(false);
  const [filter, setFilter] = useState("Tous");
  /** Onglet actif sur la page Référentiels (navigation latérale et recherche). */
  const [referentielsTab, setReferentielsTab] = useState("personnes");

  const REFERENTIELS_CONFIG_TABS = useMemo(() => new Set(["personnes", "categories", "activites", "doctypes"]), []);
  useEffect(() => {
    if (page === "referentiels" && !REFERENTIELS_CONFIG_TABS.has(referentielsTab)) {
      setReferentielsTab("personnes");
    }
  }, [page, referentielsTab, REFERENTIELS_CONFIG_TABS]);
  const [toast, setToast] = useState(null);
  const [domainDeclarations, setDomainDeclarations] = useState(() => loadDeclarations());
  const setDomainDeclarationsWithPersist = (v) => {
    const next = typeof v === "function" ? v(domainDeclarations) : v;
    setDomainDeclarations(next);
    try { localStorage.setItem("rgpd_domain_declarations", JSON.stringify(next)); } catch (_) {}
  };

  const [companyProfile, setCompanyProfile] = useState(() => loadCompanyProfile());
  const setCompanyProfileWithPersist = (v) => {
    const next = typeof v === "function" ? v(companyProfile) : v;
    setCompanyProfile(next);
    try { localStorage.setItem(COMPANY_PROFILE_STORAGE_KEY, JSON.stringify(next)); } catch (_) {}
  };
  const nextId = useRef(10);
  const isHydratingRef = useRef(false);

  const bumpProofNextIdFromList = (list) => {
    const nums = (Array.isArray(list) ? list : []).map((p) => Number(p?.id)).filter((n) => Number.isFinite(n) && n >= 0);
    if (nums.length === 0) return;
    const max = Math.max(...nums);
    nextId.current = Math.max(nextId.current, max + 1);
  };
  const pointsCollecteRef = useRef(initialPointsCollecte);
  const retraitsRef = useRef(initialRetraits);
  const syncTimersRef = useRef({});
  const toastTimerRef = useRef(null);

  const searchData = useMemo(
    () => ({
      personnes,
      traitements,
      sousTraitants,
      pointsCollecte,
      retraits,
      incidents,
      droits,
      proofs,
      activites: activites.filter((a) => a.enabled !== false),
      elementsSI,
    }),
    [personnes, traitements, sousTraitants, pointsCollecte, retraits, incidents, droits, proofs, activites, elementsSI]
  );

  const activeActivites = useMemo(() => activites.filter((a) => a.enabled !== false), [activites]);

  const criticalActions = useMemo(
    () =>
      buildCriticalActions({ traitements, retraits, proofs, sousTraitants, incidents, droits }).slice(0, 8),
    [traitements, retraits, proofs, sousTraitants, incidents, droits]
  );

  const resolveNextState = (current, updaterOrValue) =>
    typeof updaterOrValue === "function" ? updaterOrValue(current) : updaterOrValue;

  const showToast = (type, message) => {
    setToast({ type, message });
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => setToast(null), 2200);
  };

  const scheduleSync = (key, syncFn, successMessage, delayMs = 500) => {
    if (syncTimersRef.current[key]) {
      clearTimeout(syncTimersRef.current[key]);
    }

    syncTimersRef.current[key] = setTimeout(async () => {
      try {
        await syncFn();
        showToast("success", successMessage);
      } catch (error) {
        showToast("error", error?.message || "Erreur de synchronisation");
      } finally {
        delete syncTimersRef.current[key];
      }
    }, delayMs);
  };

  const loadPersons = async () => {
    const list = await personsApi.list();
    setPersonnes(Array.isArray(list) ? list : []);
  };

  const loadProcessors = async () => {
    const list = await processorsApi.list();
    setSousTraitants(Array.isArray(list) ? list : []);
  };

  const loadProcessingActivities = async () => {
    const myGen = ++processingActivitiesFetchGenRef.current;
    const raw = await processingActivitiesApi.list();
    if (myGen !== processingActivitiesFetchGenRef.current) return;
    let list = normalizeProcessingActivitiesList(raw);
    if (list.length === 0) {
      const defaultActivity = buildDefaultSolutionProcessingActivity();
      await processingActivitiesApi.sync([defaultActivity]);
      const refreshedRaw = await processingActivitiesApi.list();
      if (myGen !== processingActivitiesFetchGenRef.current) return;
      list = normalizeProcessingActivitiesList(refreshedRaw);
    }
    traitementsRef.current = list;
    setTraitements(list);
  };

  const loadConsentData = async () => {
    const data = await consentApi.getData();
    const points = Array.isArray(data?.pointsCollecte) ? data.pointsCollecte : [];
    const withdrawals = Array.isArray(data?.retraits) ? data.retraits : [];
    setPointsCollecte(points);
    setRetraits(withdrawals);
  };

  const loadIncidents = async () => {
    const list = await incidentsApi.list();
    setIncidents(Array.isArray(list) ? list : []);
  };

  const loadRequests = async () => {
    const list = await requestsApi.list();
    setDroits(Array.isArray(list) ? list : []);
  };

  const loadEvidences = async () => {
    const list = await evidencesApi.list();
    const arr = Array.isArray(list) ? list : [];
    setProofs(arr);
    bumpProofNextIdFromList(arr);
  };

  const loadDocumentCategories = async () => {
    const list = await documentCategoriesApi.list();
    setCategories(Array.isArray(list) ? list : []);
  };

  const loadActivities = async () => {
    const list = await activitiesApi.list();
    setActivites(Array.isArray(list) ? list : []);
  };

  const loadRightsWorkflow = async () => {
    const item = await rightsWorkflowApi.get();
    setProcessusDroits(item && typeof item === "object" ? item : initialProcessusDroits);
  };

  const loadPssi = async () => {
    const item = await pssiApi.get();
    if (item && typeof item === "object" && Object.keys(item).length > 0) {
      setPssi(item);
    }
  };

  const loadItAssets = async () => {
    const list = await itAssetsApi.list();
    setElementsSI(Array.isArray(list) ? list : []);
  };

  const loadPra = async () => {
    const item = await praApi.get();
    setPra(item && typeof item === "object" ? item : initialPRA);
  };

  const loadRaciMatrix = async () => {
    const matrix = await raciApi.get();
    setRaciMatrix(matrix && typeof matrix === "object" ? matrix : {});
  };

  const loadAllTenantModuleData = async () => {
    await loadPersons();
    await loadProcessors();
    await loadProcessingActivities();
    await loadConsentData();
    await loadIncidents();
    await loadRequests();
    await loadEvidences();
    await loadDocumentCategories();
    await loadActivities();
    await loadRaciMatrix();
    await loadRightsWorkflow();
    await loadPssi();
    await loadItAssets();
    await loadPra();
  };

  const applySessionPayload = (payload) => {
    if (payload?.user) {
      userStorage.set(payload.user);
      setCurrentUser(payload.user);
    }
    if (payload?.tenant) {
      tenantStorage.set(payload.tenant);
      setSessionTenant(payload.tenant);
      setMode(payload.tenant.app_mode === "light" ? "express" : "complet");
      const cp = payload.tenant.company_profile;
      setCompanyProfileWithPersist({
        ...emptyCompanyProfile,
        ...(cp && typeof cp === "object" ? cp : {}),
      });
    }
    if (payload?.organizations != null && Array.isArray(payload.organizations)) {
      organizationsStorage.set(payload.organizations);
      setOrganizations(payload.organizations);
    }
    if (payload?.roles != null && Array.isArray(payload.roles)) {
      tenantRolesStorage.set(payload.roles);
      setTenantRoles(payload.roles);
    }
    if (payload && Object.prototype.hasOwnProperty.call(payload, "read_only")) {
      setSessionReadOnly(!!payload.read_only);
    }
  };

  useEffect(() => {
    pointsCollecteRef.current = pointsCollecte;
  }, [pointsCollecte]);

  useEffect(() => {
    retraitsRef.current = retraits;
  }, [retraits]);

  useEffect(() => {
    let active = true;

    const bootstrap = async () => {
      if (!authToken) {
        setAuthLoading(false);
        return;
      }

      try {
        isHydratingRef.current = true;
        const me = await authApi.me();
        if (!active) return;
        applySessionPayload(me);
        if (!me?.tenant) {
          setMode("complet");
        }
        await loadAllTenantModuleData();
      } catch (error) {
        if (!active) return;
        tokenStorage.clear();
        trustedDeviceStorage.clear();
        userStorage.clear();
        tenantStorage.clear();
        organizationsStorage.clear();
        tenantRolesStorage.clear();
        setAuthToken(null);
        setCurrentUser(null);
        setSessionTenant(null);
        setOrganizations([]);
        setTenantRoles([]);
        setAuthError("Session expirée, reconnecte-toi.");
        setSessionReadOnly(false);
      } finally {
        if (active) {
          isHydratingRef.current = false;
          setAuthLoading(false);
        }
      }
    };

    bootstrap();
    return () => {
      active = false;
    };
    // Intentionnel : bootstrap uniquement au changement de jeton (session), pas à chaque rendu de helpers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const orgDeleteConfirmRef = useRef(false);

  /** Lien e-mail de suppression : même si l’utilisateur est encore connecté, exécuter la confirmation puis rafraîchir la session. */
  useEffect(() => {
    if (!authToken || authLoading) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("page") !== "confirm-org-delete") return;
    const rawToken = params.get("token");
    if (!rawToken?.trim()) return;
    if (orgDeleteConfirmRef.current) return;
    orgDeleteConfirmRef.current = true;

    let cancelled = false;
    const url = new URL(window.location.href);
    url.searchParams.delete("token");
    url.searchParams.delete("page");

    const stripUrl = () => {
      const q = url.searchParams.toString();
      window.history.replaceState({}, "", url.pathname + (q ? `?${q}` : ""));
    };

    (async () => {
      try {
        const data = await authApi.confirmOrganizationDeletion(rawToken.trim());
        const msg = data?.message || "Organisation supprimée.";
        if (cancelled) return;
        try {
          const me = await authApi.me();
          if (cancelled) return;
          applySessionPayload(me);
          await loadAllTenantModuleData();
          stripUrl();
          setPage("dashboard");
          showToast("success", "Organisation supprimée — liste à jour.");
          orgDeleteConfirmRef.current = false;
        } catch {
          if (cancelled) return;
          tokenStorage.clear();
          trustedDeviceStorage.clear();
          userStorage.clear();
          tenantStorage.clear();
          organizationsStorage.clear();
          tenantRolesStorage.clear();
          setAuthToken(null);
          setCurrentUser(null);
          setSessionTenant(null);
          setOrganizations([]);
          setTenantRoles([]);
          setAuthError("");
          setLoginSuccessMessage(`${msg} Reconnectez-vous si besoin.`);
          stripUrl();
          orgDeleteConfirmRef.current = false;
        }
      } catch (e) {
        orgDeleteConfirmRef.current = false;
        if (!cancelled) {
          showToast("error", e?.message || "Confirmation impossible.");
          stripUrl();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- helpers stables volontairement
  }, [authToken, authLoading]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.storageArea !== localStorage) return;
      if (e.key === "rgpd_api_token" && !e.newValue) {
        setAuthToken(null);
        setCurrentUser(null);
        setSessionTenant(null);
        setOrganizations([]);
        setTenantRoles([]);
        setAuthLoading(false);
      }
      if (e.key === "rgpd_organizations") {
        if (!e.newValue) {
          setOrganizations([]);
        } else {
          try {
            const parsed = JSON.parse(e.newValue);
            if (Array.isArray(parsed)) setOrganizations(parsed);
          } catch {
            /* ignore */
          }
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    const syncTimers = syncTimersRef.current;
    return () => {
      Object.values(syncTimers).forEach((timer) => clearTimeout(timer));
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const handleLoginSuccess = (payload) => {
    setAuthError("");
    setLoginSuccessMessage("");
    setSessionReadOnly(false);
    if (payload?.trusted_device_token) {
      trustedDeviceStorage.set(payload.trusted_device_token);
    }
    tokenStorage.set(payload.token);
    applySessionPayload(payload);
    setAuthToken(payload.token);
    setAuthLoading(true);
  };

  const handleSwitchOrganization = async (tenantId) => {
    if (sessionTenant?.id === tenantId) return;
    setOrgMenuBusy(true);
    try {
      isHydratingRef.current = true;
      const payload = await authApi.switchTenant(tenantId);
      applySessionPayload({
        tenant: payload.tenant,
        organizations: payload.organizations,
        roles: payload.roles,
      });
      await loadAllTenantModuleData();
    } catch (error) {
      showToast("error", error?.message || "Changement d’organisation impossible.");
    } finally {
      isHydratingRef.current = false;
      setOrgMenuBusy(false);
    }
  };

  const handleRequestOrganizationDeletion = async (tenantId) => {
    try {
      const data = await authApi.requestOrganizationDeletion(tenantId);
      showToast("success", data?.message || "Demande enregistrée.");
    } catch (error) {
      showToast("error", error?.message || "Demande impossible.");
    }
  };

  const handleCreateOrganization = async (name, appMode = "complet") => {
    setOrgMenuBusy(true);
    try {
      isHydratingRef.current = true;
      const payload = await authApi.createOrganization({ name, app_mode: appMode });
      applySessionPayload(payload);
      await loadAllTenantModuleData();
      showToast("success", "Organisation créée — vous y êtes basculé.");
    } catch (error) {
      showToast("error", error?.message || "Création impossible.");
    } finally {
      isHydratingRef.current = false;
      setOrgMenuBusy(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    } finally {
      tokenStorage.clear();
      trustedDeviceStorage.clear();
      userStorage.clear();
      tenantStorage.clear();
      organizationsStorage.clear();
      tenantRolesStorage.clear();
      setAuthToken(null);
      setCurrentUser(null);
      setSessionTenant(null);
      setOrganizations([]);
      setTenantRoles([]);
      setMode(null);
      setSessionReadOnly(false);
    }
  };

  const handleCreatePersonne = async (payload) => {
    const created = await personsApi.create(payload);
    setPersonnes((prev) => [created, ...prev]);
    showToast("success", "Personne enregistrée");
  };

  const handleUpdatePersonne = async (id, payload) => {
    const updated = await personsApi.update(id, payload);
    setPersonnes((prev) => prev.map((item) => (item.id === id ? updated : item)));
    showToast("success", "Personne mise à jour");
  };

  const handleDeletePersonne = async (id) => {
    await personsApi.remove(id);
    setPersonnes((prev) => prev.filter((item) => item.id !== id));
    showToast("success", "Personne supprimée");
  };

  const handleGenerateReport = async (type) => {
    try {
      if (type === "raci") {
        await activitiesApi.sync(activites);
        await raciApi.sync(buildRaciMatrixPayload(activeActivites, raciMatrix));
      }

      const data = await reportsApi.generate(type);
      showToast("success", reportGenerateSuccessToastMessage(data));
      setPage("reports-list");
    } catch (error) {
      showToast("error", error.message || "Impossible de générer le rapport");
    }
  };

  const handleDownloadExpressReport = async () => {
    if (downloadingExpressReport) return;
    setDownloadingExpressReport(true);
    try {
      const data = await reportsApi.generate("dossier_hub_rgpd");
      const reportId = data?.item?.id;
      const generatedName = data?.item?.downloadFileName;
      if (!reportId) throw new Error("Rapport généré, mais identifiant introuvable.");
      const { blob, filename } = await reportsApi.downloadPdf(reportId, { preferredFileName: generatedName });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename || `dossier-audit-express-${reportId}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      showToast(
        "success",
        generatedName ? `Dossier Audit Express téléchargé (${generatedName})` : "Dossier Audit Express téléchargé."
      );
    } catch (error) {
      showToast("error", error.message || "Impossible de télécharger le dossier Audit Express.");
    } finally {
      setDownloadingExpressReport(false);
    }
  };

  const setSousTraitantsWithSync = (updaterOrValue) => {
    setSousTraitants((current) => {
      const next = resolveNextState(current, updaterOrValue);
      if (!isHydratingRef.current) {
        scheduleSync("processors", async () => {
          await processorsApi.sync(next);
          await loadProcessors();
        }, "Sous-traitants sauvegardés");
      }
      return next;
    });
  };

  const setTraitementsWithSync = (updaterOrValue) => {
    setTraitements((current) => {
      const next = resolveNextState(current, updaterOrValue);
      traitementsRef.current = next;
      if (!isHydratingRef.current) {
        scheduleSync(
          "traitements",
          async () => {
            await processingActivitiesApi.sync(traitementsRef.current);
            await loadProcessingActivities();
          },
          "Traitements sauvegardés",
          120
        );
      }
      return next;
    });
  };

  const setPointsCollecteWithSync = (updaterOrValue) => {
    setPointsCollecte((current) => {
      const next = resolveNextState(current, updaterOrValue);
      pointsCollecteRef.current = next;
      if (!isHydratingRef.current) {
        scheduleSync(
          "consentements",
          async () => {
            await consentApi.syncData(next, retraitsRef.current);
            await loadConsentData();
          },
          "Consentements sauvegardés"
        );
      }
      return next;
    });
  };

  const setRetraitsWithSync = (updaterOrValue) => {
    setRetraits((current) => {
      const next = resolveNextState(current, updaterOrValue);
      retraitsRef.current = next;
      if (!isHydratingRef.current) {
        scheduleSync(
          "consentements",
          async () => {
            await consentApi.syncData(pointsCollecteRef.current, next);
            await loadConsentData();
          },
          "Consentements sauvegardés"
        );
      }
      return next;
    });
  };

  const setIncidentsWithSync = (updaterOrValue) => {
    setIncidents((current) => {
      const next = resolveNextState(current, updaterOrValue);
      if (!isHydratingRef.current) {
        scheduleSync("incidents", () => incidentsApi.sync(next), "Incidents sauvegardés");
      }
      return next;
    });
  };

  const setDroitsWithSync = (updaterOrValue) => {
    setDroits((current) => {
      const next = resolveNextState(current, updaterOrValue);
      if (!isHydratingRef.current) {
        scheduleSync("droits", async () => {
          await requestsApi.sync(next);
          await loadRequests();
        }, "Demandes de droits sauvegardées");
      }
      return next;
    });
  };

  const setProofsWithSync = (updaterOrValue) => {
    setProofs((current) => {
      const next = resolveNextState(current, updaterOrValue);
      if (!isHydratingRef.current) {
        scheduleSync("preuves", async () => {
          await evidencesApi.sync(next);
          await loadEvidences();
        }, "Preuves sauvegardées");
      }
      return next;
    });
  };

  const setCategoriesWithSync = (updaterOrValue) => {
    setCategories((current) => {
      const next = resolveNextState(current, updaterOrValue);
      if (!Array.isArray(next)) {
        return current;
      }
      if (!isHydratingRef.current) {
        scheduleSync("categories", async () => {
          const isFullClear = next.length === 0;
          try {
            await documentCategoriesApi.sync(next);
            await loadDocumentCategories();
          } catch (error) {
            // Si on tente un "vidage total", on resynchronise quand même avec le serveur
            // pour éviter de laisser l'UI dans un état local vide incohérent.
            if (isFullClear) {
              try {
                await loadDocumentCategories();
              } catch (_) {
                // no-op: on relance l'erreur initiale ensuite
              }
            }
            throw error;
          }
        }, "Catégories sauvegardées");
      }
      return next;
    });
  };

  const setActivitesWithSync = (updaterOrValue) => {
    setActivites((current) => {
      const next = resolveNextState(current, updaterOrValue);
      if (!isHydratingRef.current) {
        scheduleSync("activites", async () => {
          await activitiesApi.sync(next);
          await loadActivities();
          await loadRaciMatrix();
        }, "Activités sauvegardées");
      }
      return next;
    });
  };

  const setProcessusDroitsWithSync = (updaterOrValue) => {
    setProcessusDroits((current) => {
      const next = resolveNextState(current, updaterOrValue);
      if (!isHydratingRef.current) {
        scheduleSync("workflow-droits", () => rightsWorkflowApi.sync(next), "Configuration des droits sauvegardée");
      }
      return next;
    });
  };

  const setPssiWithSync = (updaterOrValue) => {
    setPssi((current) => {
      const next = resolveNextState(current, updaterOrValue);
      if (!isHydratingRef.current) {
        scheduleSync("pssi", () => pssiApi.sync(next), "PSSI sauvegardée");
      }
      return next;
    });
  };

  const setElementsSIWithSync = (updaterOrValue) => {
    setElementsSI((current) => {
      const next = resolveNextState(current, updaterOrValue);
      if (!isHydratingRef.current) {
        scheduleSync("inventaire-si", () => itAssetsApi.sync(next), "Inventaire SI sauvegardé");
      }
      return next;
    });
  };

  const setPraWithSync = (updaterOrValue) => {
    setPra((current) => {
      const next = resolveNextState(current, updaterOrValue);
      if (!isHydratingRef.current) {
        scheduleSync("pra", () => praApi.sync(next), "PRA sauvegardé");
      }
      return next;
    });
  };

  const setRaciMatrixWithSync = (updaterOrValue) => {
    setRaciMatrix((current) => {
      const next = resolveNextState(current, updaterOrValue);
      if (!isHydratingRef.current) {
        scheduleSync("raci", () => raciApi.sync(next), "Matrice RACI sauvegardée");
      }
      return next;
    });
  };

  const handleSave = form => {
    const ed = editingProof;
    if (ed) {
      setProofsWithSync(ps => ps.map(p => p.id === ed.id ? { ...form, id: ed.id } : p));
    } else {
      setProofsWithSync(ps => {
        const nums = ps.map(p => Number(p?.id)).filter(n => Number.isFinite(n));
        const maxExisting = nums.length ? Math.max(...nums) : 0;
        const newId = maxExisting + 1;
        nextId.current = Math.max(nextId.current, newId + 1);
        return [...ps, { ...form, id: newId }];
      });
    }
    setShowForm(false);
    setEditingProof(null);
  };

  const handleExpressNavigate = (targetPage, action) => {
    setExpressAction({ page: targetPage, action });
    if (typeof targetPage === "string" && targetPage.startsWith("referentiels-")) {
      setReferentielsTab(targetPage.slice("referentiels-".length));
      setPage("referentiels");
      return;
    }
    const nextPage = targetPage === "reports" ? "reports-list" : targetPage;
    setPage(nextPage);
  };

  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: APP_FONT_FAMILY }}>
        Chargement...
      </div>
    );
  }

  if (!authToken) {
    const guestPage = new URLSearchParams(window.location.search).get("page");
    if (guestPage === "forgot-password") {
      return <ForgotPasswordPage />;
    }
    if (guestPage === "reset-password") {
      return <ResetPasswordPage />;
    }
    if (guestPage === "confirm-org-delete") {
      return <ConfirmOrganizationDeletionPage />;
    }
    return <LoginPage onLoginSuccess={handleLoginSuccess} error={authError} successInfo={loginSuccessMessage} />;
  }

  const needsOrgOnboarding =
    !!sessionTenant &&
    (sessionTenant.onboarding_completed_at === null ||
      sessionTenant.onboarding_completed_at === undefined ||
      sessionTenant.onboarding_completed_at === "");

  const handleOnboardingCompleted = async () => {
    try {
      const me = await authApi.me();
      applySessionPayload(me);
      await loadAllTenantModuleData();
      showToast("success", "Organisation configurée.");
    } catch (e) {
      showToast("error", e?.message || "Impossible de rafraîchir la session.");
    }
  };

  const wrapWithOnboarding = (node) => (
    <>
      {needsOrgOnboarding && (
        <HapivotOnboardingWizard
          currentUser={currentUser}
          organizationName={sessionTenant?.name}
          existingCompanyProfile={sessionTenant?.company_profile}
          onCompleted={handleOnboardingCompleted}
          onToast={showToast}
        />
      )}
      <DocTypesEnabledProvider sessionReadOnly={sessionReadOnly} tenantId={sessionTenant?.id ?? null}>
        {node}
      </DocTypesEnabledProvider>
    </>
  );

  const toastNode = toast ? (
    <div
      style={{
        position: "fixed",
        top: 88,
        right: 18,
        zIndex: 9999,
        padding: "10px 14px",
        borderRadius: 10,
        fontSize: 12,
        fontWeight: 700,
        border: `1px solid ${toast.type === "success" ? "#86efac" : "#fca5a5"}`,
        background: toast.type === "success" ? "#ecfdf3" : "#fff1f2",
        color: toast.type === "success" ? "#065f46" : "#991b1b",
        boxShadow: "0 6px 20px rgba(16,24,40,0.12)",
      }}
    >
      {toast.message}
    </div>
  ) : null;

  const handleNavigate = (targetPage, context) => {
    if (typeof targetPage === "string" && targetPage.startsWith("referentiels-")) {
      setReferentielsTab(targetPage.slice("referentiels-".length));
      setPage("referentiels");
      if (context && (context.id != null || context.searchTerm || context.itemType)) {
        setSearchHighlight({
          id: context.id,
          searchTerm: context.searchTerm || null,
          itemType: context.itemType || null,
        });
      } else {
        setSearchHighlight(null);
      }
      return;
    }

    const nextPage = targetPage === "reports" ? "reports-list" : targetPage;
    setPage(nextPage);

    if (nextPage === "referentiels" && context) {
      if (context.referentielsTab) setReferentielsTab(context.referentielsTab);
      else if (context.itemType === "personne") setReferentielsTab("personnes");
      else if (context.itemType === "activite") setReferentielsTab("activites");
    }

    if (context && (context.id != null || context.searchTerm || context.itemType)) {
      setSearchHighlight({
        id: context.id,
        searchTerm: context.searchTerm || null,
        itemType: context.itemType || null,
      });
    } else {
      setSearchHighlight(null);
    }
  };

  const refreshSessionUser = async () => {
    const me = await authApi.me();
    applySessionPayload(me);
  };

  const renderWithNav = content => (
    <div className="ta-shell" style={{ fontFamily: APP_FONT_FAMILY }}>
      {sessionReadOnly ? (
        <div
          role="status"
          style={{
            background: "#1e3a5f",
            color: "#e0e7ff",
            padding: "10px 20px",
            fontSize: 13,
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          Consultation en lecture seule — les enregistrements et changements d’organisation sont désactivés.
        </div>
      ) : null}
      <AppNav
        mode={mode}
        page={page}
        referentielsTab={referentielsTab}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        currentUser={currentUser}
        criticalActions={criticalActions}
        searchData={searchData}
        sessionTenant={sessionTenant}
        organizations={organizations}
        tenantRoles={tenantRoles}
        onSwitchOrganization={handleSwitchOrganization}
        onCreateOrganization={handleCreateOrganization}
        onRequestDeleteOrganization={handleRequestOrganizationDeletion}
        orgMenuBusy={orgMenuBusy}
        onOpenAccount={sessionReadOnly ? undefined : () => handleNavigate("account")}
        sessionReadOnly={sessionReadOnly}
      />
      <div className="ta-content">{content}</div>
      {toastNode}
    </div>
  );

  const standardPageContent = {
    pssi: <PagePSSI pssi={pssi} setPssi={setPssiWithSync} />,
    "inventaire-si": <PageInventaireSI elementsSI={elementsSI} setElementsSI={setElementsSIWithSync} personnes={personnes} sousTraitants={sousTraitants} searchHighlight={searchHighlight} />,
    pra: <PagePRA pra={pra} setPra={setPraWithSync} personnes={personnes} />,
    consentement: <PageConsentement pointsCollecte={pointsCollecte} setPointsCollecte={setPointsCollecteWithSync} retraits={retraits} setRetraits={setRetraitsWithSync} personnes={personnes} traitements={traitements} searchHighlight={searchHighlight} />,
    ropa: (
      <PageROPA
        traitements={traitements}
        setTraitements={setTraitementsWithSync}
        personnes={personnes}
        sousTraitants={sousTraitants}
        mode={mode}
        expressAction={expressAction}
        onClearExpressAction={() => setExpressAction(null)}
        searchHighlight={searchHighlight}
        onTraitementsReload={loadProcessingActivities}
        onToast={showToast}
      />
    ),
    "ropa-flux": (
      <PageCarteFluxTraitements
        traitements={traitements}
        pointsCollecte={pointsCollecte}
        organizationName={sessionTenant?.name}
        onOpenRegistre={() => handleNavigate("ropa")}
      />
    ),
    referentiels: (
      <PageReferentiels
        personnes={personnes}
        setPersonnes={setPersonnes}
        categories={categories}
        setCategories={setCategoriesWithSync}
        activites={activites}
        setActivites={setActivitesWithSync}
        proofs={proofs}
        navTab={referentielsTab}
        onBack={() => handleNavigate("preuves")}
        onCreatePersonne={handleCreatePersonne}
        onUpdatePersonne={handleUpdatePersonne}
        onDeletePersonne={handleDeletePersonne}
        searchHighlight={searchHighlight}
      />
    ),
    "registre-sous-traitants": (
      <PageRegistreSousTraitants
        sousTraitants={sousTraitants}
        setSousTraitants={setSousTraitantsWithSync}
        personnes={personnes}
        proofs={proofs}
        searchHighlight={searchHighlight}
        onBack={() => handleNavigate("dashboard")}
      />
    ),
    "matrice-raci": (
      <PageMatriceRaci
        activites={activeActivites}
        personnes={personnes}
        raciMatrix={raciMatrix}
        setRaciMatrix={setRaciMatrixWithSync}
        categories={categories}
        onBack={() => handleNavigate("dashboard")}
      />
    ),
    incidents: <PageIncidents incidents={incidents} setIncidents={setIncidentsWithSync} personnes={personnes} sousTraitants={sousTraitants} searchHighlight={searchHighlight} onToast={showToast} />,
    droits: <PageDroits droits={droits} setDroits={setDroitsWithSync} personnes={personnes} searchHighlight={searchHighlight} />,
    "dashboard-config": (
      <PageDashboardConfig
        domainDeclarations={domainDeclarations}
        setDomainDeclarations={setDomainDeclarationsWithPersist}
        onBack={() => handleNavigate("dashboard")}
      />
    ),
    "societe-config": (
      <PageSocieteConfig
        companyProfile={companyProfile}
        setCompanyProfile={setCompanyProfileWithPersist}
        onBack={() => handleNavigate("dashboard")}
      />
    ),
    "team-config": <PageTeamConfig onToast={showToast} onBack={() => handleNavigate("dashboard")} />,
    "droits-config": (
      <PageConfigDroits
        processusDroits={processusDroits}
        setProcessusDroits={setProcessusDroitsWithSync}
        personnes={personnes}
        habColor={habColor}
        onBack={() => setPage("dashboard")}
      />
    ),
    "reports-list": (
      <PageReportsList
        currentUser={currentUser}
        onToast={showToast}
        onOpenGenerationPage={() => handleNavigate("reports-config")}
      />
    ),
    "reports-config": (
      <PageReportsConfig
        tenantId={sessionTenant?.id ?? null}
        onToast={showToast}
        onGenerateRaci={() => handleGenerateReport("raci")}
        onOpenListPage={() => handleNavigate("reports-list")}
      />
    ),
    account: (
      <PageAccount
        currentUser={currentUser}
        onSessionRefresh={refreshSessionUser}
        onToast={showToast}
        onBack={() => handleNavigate("dashboard")}
      />
    ),
  };

  if (standardPageContent[page]) return wrapWithOnboarding(renderWithNav(standardPageContent[page]));

  if (page === "preuves") {
    const searchData = { personnes, traitements, sousTraitants, pointsCollecte, retraits, incidents, droits, proofs, activites: activeActivites, elementsSI };
    const filtered = filter === "Tous" ? proofs : proofs.filter(p => p.statut === filter);
    const stats = {
      total: proofs.length,
      validees: proofs.filter(p => p.statut === "Validé").length,
      enCours: proofs.filter(p => p.statut === "En cours").length,
      nonConformes: proofs.filter(p => p.statut === "Non conforme").length,
    };

    return wrapWithOnboarding(
      <div className="ta-shell" style={{ minHeight: "100vh", background: COLORS.backgroundApp, fontFamily: APP_FONT_FAMILY }}>
        {sessionReadOnly ? (
          <div
            role="status"
            style={{
              background: "#1e3a5f",
              color: "#e0e7ff",
              padding: "10px 20px",
              fontSize: 13,
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            Consultation en lecture seule — les enregistrements et changements d’organisation sont désactivés.
          </div>
        ) : null}
        <AppNav
        mode={mode}
        page={page}
        referentielsTab={referentielsTab}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        currentUser={currentUser}
        criticalActions={criticalActions}
        searchData={searchData}
        sessionTenant={sessionTenant}
        organizations={organizations}
        tenantRoles={tenantRoles}
        onSwitchOrganization={handleSwitchOrganization}
        onCreateOrganization={handleCreateOrganization}
        onRequestDeleteOrganization={handleRequestOrganizationDeletion}
        orgMenuBusy={orgMenuBusy}
        onOpenAccount={sessionReadOnly ? undefined : () => handleNavigate("account")}
        sessionReadOnly={sessionReadOnly}
      />
        <div className="ta-content">
        <PageShell>
          <PageHeader
            title="Dossier Preuve RGPD — Export Audit"
            subtitle="Conformité RGPD — Accountability art. 5(2)"
            actions={
              <>
                <button type="button" className="page-shell__btn page-shell__btn--ghost" onClick={() => setShowExport(true)}>
                  <MdDescription size={16} /> Aperçu PDF
                </button>
                <button type="button" className="page-shell__btn page-shell__btn--primary" onClick={() => { setEditingProof(null); setShowForm(true); }}>
                  + Nouvelle preuve
                </button>
              </>
            }
          />

          <PageContent>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 22 }}>
          {[
            { label: "Total preuves", val: stats.total, color: "#1e3a5f", bg: "#eff6ff", border: "#bfdbfe", f: "Tous" },
            { label: "Validées", val: stats.validees, color: "#065f46", bg: "#d1fae5", border: "#6ee7b7", f: "Validé" },
            { label: "En cours", val: stats.enCours, color: "#92400e", bg: "#fef3c7", border: "#fcd34d", f: "En cours" },
            { label: "Non conformes", val: stats.nonConformes, color: "#991b1b", bg: "#fee2e2", border: "#fca5a5", f: "Non conforme" },
          ].map(item => (
            <div key={item.label} onClick={() => setFilter(item.f)} style={{ background: item.bg, borderRadius: 10, padding: "14px 18px", border: `1.5px solid ${item.border}`, cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
              onMouseLeave={e => e.currentTarget.style.transform = "none"}>
              <div style={{ fontSize: 28, fontWeight: 800, color: item.color, lineHeight: 1 }}>{item.val}</div>
              <div style={{ fontSize: 12, color: item.color, marginTop: 3, fontWeight: 600 }}>{item.label}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600 }}>Types :</span>
          {DOC_TYPES.map(dt => <DocTypeBadge key={dt.code} code={dt.code} />)}
        </div>
        <div style={{ display: "flex", gap: 8, marginBottom: 18, alignItems: "center" }}>
          <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>Filtrer :</span>
          {["Tous", "Validé", "En cours", "Non conforme", "Archivé"].map(s => (
            <button key={s} onClick={() => setFilter(s)} style={{ padding: "4px 14px", borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: "pointer", background: filter === s ? "#1e3a5f" : "#fff", color: filter === s ? "#fff" : "#374151", border: `1.5px solid ${filter === s ? "#1e3a5f" : "#e5e7eb"}` }}>{s}</button>
          ))}
        </div>
        {showForm && <ProofForm initial={editingProof} onSave={handleSave} onCancel={() => { setShowForm(false); setEditingProof(null); }} personnes={personnes} categories={categories} activites={activeActivites} />}
        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0", color: "#9ca3af", fontSize: 14 }}>Aucune preuve "{filter}" — <button onClick={() => { setEditingProof(null); setShowForm(true); }} style={{ color: "#2563eb", background: "none", border: "none", cursor: "pointer", fontWeight: 700 }}>Créer la première</button></div>
        ) : filtered.map(p => (
          <ProofCard key={p.id} proof={p} highlightId={searchHighlight?.id} highlightItemType={searchHighlight?.itemType ?? null} onEdit={proof => { setEditingProof(proof); setShowForm(true); }} onDelete={id => setProofsWithSync(ps => ps.filter(p => p.id !== id))} personnes={personnes} />
        ))}
          </PageContent>
          {showExport && <ExportPreview proofs={proofs} personnes={personnes} onClose={() => setShowExport(false)} />}
        </PageShell>
        </div>
      {toastNode}
    </div>
    );
  }

  // Dashboard routing (default fallback)
  if (mode === "express") {
    return wrapWithOnboarding(
      <div className="ta-shell" style={{ fontFamily: APP_FONT_FAMILY }}>
        {sessionReadOnly ? (
          <div
            role="status"
            style={{
              background: "#1e3a5f",
              color: "#e0e7ff",
              padding: "10px 20px",
              fontSize: 13,
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            Consultation en lecture seule — les enregistrements et changements d’organisation sont désactivés.
          </div>
        ) : null}
        <AppNav
        mode={mode}
        page={page}
        referentielsTab={referentielsTab}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        currentUser={currentUser}
        criticalActions={criticalActions}
        searchData={searchData}
        sessionTenant={sessionTenant}
        organizations={organizations}
        tenantRoles={tenantRoles}
        onSwitchOrganization={handleSwitchOrganization}
        onCreateOrganization={handleCreateOrganization}
        onRequestDeleteOrganization={handleRequestOrganizationDeletion}
        orgMenuBusy={orgMenuBusy}
        onOpenAccount={sessionReadOnly ? undefined : () => handleNavigate("account")}
        sessionReadOnly={sessionReadOnly}
      />
        <div className="ta-content">
          <DashboardExpress
            traitements={traitements}
            sousTraitants={sousTraitants}
            pointsCollecte={pointsCollecte}
            droits={droits}
            processusDroits={processusDroits}
            onNavigate={handleExpressNavigate}
            onDownloadAuditExpress={handleDownloadExpressReport}
            downloadingAuditReport={downloadingExpressReport}
          />
        </div>
        {toastNode}
      </div>
    );
  }
  
  return wrapWithOnboarding(
    <div className="ta-shell" style={{ fontFamily: APP_FONT_FAMILY }}>
      {sessionReadOnly ? (
        <div
          role="status"
          style={{
            background: "#1e3a5f",
            color: "#e0e7ff",
            padding: "10px 20px",
            fontSize: 13,
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          Consultation en lecture seule — les enregistrements et changements d’organisation sont désactivés.
        </div>
      ) : null}
      <AppNav
        mode={mode}
        page={page}
        referentielsTab={referentielsTab}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        currentUser={currentUser}
        criticalActions={criticalActions}
        searchData={searchData}
        sessionTenant={sessionTenant}
        organizations={organizations}
        tenantRoles={tenantRoles}
        onSwitchOrganization={handleSwitchOrganization}
        onCreateOrganization={handleCreateOrganization}
        onRequestDeleteOrganization={handleRequestOrganizationDeletion}
        orgMenuBusy={orgMenuBusy}
        onOpenAccount={sessionReadOnly ? undefined : () => handleNavigate("account")}
        sessionReadOnly={sessionReadOnly}
      />
      <div className="ta-content">
        <PageDashboard proofs={proofs} incidents={incidents} droits={droits} sousTraitants={sousTraitants} activites={activeActivites} traitements={traitements} pointsCollecte={pointsCollecte} retraits={retraits} domainDeclarations={domainDeclarations} onNavigate={handleNavigate} priorityActions={criticalActions} />
      </div>
      {toastNode}
    </div>
  );
}
